import React, { useEffect, useState } from 'react';
import { Button, message } from 'antd';
import { CommentOutlined, PlusOutlined } from '@ant-design/icons';
import ContentModal from './ContentModal';
import FeedbackFormModal from './FeedbackFormModal';
import FeedbackTable from './FeedbackTable';
import PanelFrame from './PanelFrame';
import { useContentViewer } from './useContentViewer';
import type { PanelChrome } from './layout';
import {
	useDeleteProgramFeedbackMutation,
	useGetProgramFeedbackQuery,
} from '../../redux/api/manufacturing';
import { ProgramFeedback } from '../../interfaces/CamProgramming.interface';

/**
 * General-purpose notes on a program. Unlike the issues panel nothing here
 * claims something is wrong — it is for observations and context that do not
 * belong to a specific review.
 */
const FeedbackPanel: React.FC<{
	programId: string;
	title?: string;
	chrome?: PanelChrome;
}> = ({ programId, title = 'Feedback', chrome }) => {
	const { data, isLoading } = useGetProgramFeedbackQuery({
		program: programId,
	});
	const [deleteFeedback] = useDeleteProgramFeedbackMutation();

	const [modalOpen, setModalOpen] = useState(false);
	const [editingEntry, setEditingEntry] = useState<ProgramFeedback | null>(null);
	const viewer = useContentViewer<ProgramFeedback>();

	const entries = data?.results || [];

	useEffect(() => {
		setModalOpen(false);
		setEditingEntry(null);
	}, [programId]);

	const handleOpenCreate = () => {
		setEditingEntry(null);
		setModalOpen(true);
	};

	const handleOpenEdit = (entry: ProgramFeedback) => {
		setEditingEntry(entry);
		setModalOpen(true);
	};

	const handleDelete = async (entry: ProgramFeedback) => {
		try {
			await deleteFeedback({ id: entry.id, programId }).unwrap();
			message.success('Feedback removed');
		} catch {
			message.error('Failed to remove feedback');
		}
	};

	return (
		<>
			<PanelFrame
				chrome={chrome}
				icon={<CommentOutlined className="cam-section__icon" />}
				title={title}
				count={entries.length}
				actionPlacement="below"
				action={(expand) => (
					<Button
						icon={<PlusOutlined />}
						onClick={() => {
							expand();
							handleOpenCreate();
						}}
					>
						Log Feedback
					</Button>
				)}
			>
				<FeedbackTable
					entries={entries}
					loading={isLoading}
					onView={viewer.view}
					onEdit={handleOpenEdit}
					onDelete={handleDelete}
				/>
			</PanelFrame>

			<FeedbackFormModal
				key={editingEntry ? editingEntry.id : 'create'}
				programId={programId}
				entry={editingEntry}
				open={modalOpen}
				onClose={() => {
					setModalOpen(false);
					setEditingEntry(null);
				}}
			/>

			<ContentModal
				title="Feedback"
				html={viewer.entry?.body ?? null}
				emptyText="No content"
				open={viewer.open}
				onClose={viewer.close}
				afterClose={viewer.afterClose}
			/>
		</>
	);
};

export default FeedbackPanel;
